/**
 * 树状字段编辑器 - 工具函数和类型定义
 *
 * FieldNode 结构:
 * {
 *   id: string,
 *   key: string,
 *   valueType: 'string' | 'array' | 'object',
 *   stringValue: string,
 *   arrayValues: string[],
 *   children: FieldNode[],
 *   depth: number,
 * }
 *
 * DataFormat: 'plain-object' | 'object-array' | 'array-with-array-values' | 'object-with-array-values' | 'nested-object'
 */

let nodeIdCounter = 0;

export const generateNodeId = () => {
  nodeIdCounter += 1;
  return `node_${Date.now()}_${nodeIdCounter}`;
};

const detectValueType = (value) => {
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object' && value !== null) return 'object';
  return 'string';
};

const buildNodeFromValue = (key, value, depth) => {
  const valueType = detectValueType(value);

  if (valueType === 'array') {
    const arrayValue = value;
    const firstItem = arrayValue[0];
    if (firstItem !== null && typeof firstItem === 'object' && !Array.isArray(firstItem)) {
      const children = Object.entries(firstItem).map(([childKey, childVal]) =>
        buildNodeFromValue(childKey, childVal, depth + 1)
      );
      return {
        id: generateNodeId(),
        key,
        valueType: 'array',
        stringValue: '',
        arrayValues: [],
        children,
        depth,
      };
    }
    return {
      id: generateNodeId(),
      key,
      valueType: 'array',
      stringValue: '',
      arrayValues: arrayValue.map((item) => String(item)),
      children: [],
      depth,
    };
  }

  if (valueType === 'object') {
    const objValue = value;
    const children = Object.entries(objValue).map(([childKey, childVal]) =>
      buildNodeFromValue(childKey, childVal, depth + 1)
    );
    return {
      id: generateNodeId(),
      key,
      valueType: 'object',
      stringValue: '',
      arrayValues: [],
      children,
      depth,
    };
  }

  return {
    id: generateNodeId(),
    key,
    valueType: 'string',
    stringValue: String(value ?? ''),
    arrayValues: [],
    children: [],
    depth,
  };
};

const detectFormat = (data) => {
  if (Array.isArray(data)) {
    const firstItem = data[0];
    if (firstItem && typeof firstItem === 'object' && !Array.isArray(firstItem)) {
      const values = Object.values(firstItem);
      const hasArrayValue = values.some((v) => Array.isArray(v));
      return hasArrayValue ? 'array-with-array-values' : 'object-array';
    }
    return 'object-array';
  }

  if (typeof data === 'object' && data !== null) {
    const values = Object.values(data);
    const hasArrayValue = values.some((v) => Array.isArray(v));
    const hasObjectValue = values.some(
      (v) => typeof v === 'object' && v !== null && !Array.isArray(v)
    );
    if (hasObjectValue) return 'nested-object';
    if (hasArrayValue) return 'object-with-array-values';
    return 'plain-object';
  }

  return 'plain-object';
};

export const parseDataToNodes = (data) => {
  const format = detectFormat(data);
  let nodes = [];

  if (Array.isArray(data)) {
    const firstItem = data[0];
    if (firstItem && typeof firstItem === 'object' && !Array.isArray(firstItem)) {
      nodes = Object.entries(firstItem).map(([key, value]) =>
        buildNodeFromValue(key, value, 0)
      );
    }
  } else if (typeof data === 'object' && data !== null) {
    nodes = Object.entries(data).map(([key, value]) =>
      buildNodeFromValue(key, value, 0)
    );
  }

  return { format, nodes, originalData: data };
};

const buildValueFromNode = (node) => {
  if (node.valueType === 'string') {
    return node.stringValue;
  }

  if (node.valueType === 'array') {
    if (node.children.length > 0) {
      const childObj = {};
      node.children.forEach((child) => {
        childObj[child.key] = buildValueFromNode(child);
      });
      return [childObj];
    }
    return node.arrayValues;
  }

  if (node.valueType === 'object') {
    const obj = {};
    node.children.forEach((child) => {
      obj[child.key] = buildValueFromNode(child);
    });
    return obj;
  }

  return node.stringValue;
};

export const serializeNodesToData = (nodes, format) => {
  const obj = {};
  nodes.forEach((node) => {
    obj[node.key] = buildValueFromNode(node);
  });

  if (format === 'object-array' || format === 'array-with-array-values') {
    return [obj];
  }

  return obj;
};

export const createEmptyNode = (depth) => ({
  id: generateNodeId(),
  key: '',
  valueType: 'string',
  stringValue: '',
  arrayValues: [],
  children: [],
  depth,
});
